/* eslint-disable @typescript-eslint/no-explicit-any */

import axios from "axios";
import { authStore } from "../stores/authStore.js";
import { proxy } from "valtio";
import { AUTH_STATE_KEY } from "../utils/localStoragekeys.js";
import { normalizeUserData } from "@/utils/userNormalizer.js";
import { userDataFormat } from "@/utils/userDataFormat.js";

const BASE_URL = import.meta.env.VITE_BACKEND_URL || "http://localhost:8080";

// ---------------------------------------------
// TYPE DEFINITIONS
// ---------------------------------------------

interface UserProfile {
  [key: string]: string | number | boolean | File | undefined;
}

interface User {
  id: string;
  name: string;
  email: string;
  age?: string | number;
  gender?: string;
  aadhaarNumber?: string;
  profileImageId?: string | null;
  profileImage?: string;
  currentShipImo?: string;
  currentShipName?: string;
  indianCdc?: string;
  seafarerSidId?: string | null;
  seafarerSid?: string;
  role?: string;
  rankDesignation?: string;
  // ... include any other user-level fields here
}

interface AuthState {
  isAuthenticated: boolean;
  user?: User;
  _lastUpdated: string;
}

interface ProfileAPI {
  loading: boolean;
  error: string | null;
  fetchProfile: () => Promise<User>;
  updateProfile: (profileData: Partial<UserProfile>) => Promise<User | null>;
}

type UpdateUserResponse = {
  message: string;
  user: User;
};
// ---------------------------------------------
// PROFILE API
// ---------------------------------------------

export const profileApi = proxy<ProfileAPI>({
  loading: false,
  error: null,

  async fetchProfile() {
    try {
      const response = await axios.get<User>(`${BASE_URL}/api/users/me`, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.status === 401) {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/";
      }

      if (response.status === 200) {
        const user = normalizeUserData(response.data as any);
        const authState: AuthState =
          JSON.parse(localStorage.getItem(AUTH_STATE_KEY) || "{}") || {};
        authState.user = userDataFormat(user as any) as any;
        localStorage.setItem(AUTH_STATE_KEY, JSON.stringify(authState));

        return user;
      } else {
        throw new Error("Failed to fetch profile");
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || error.message);
      }
      throw new Error("An unknown error occurred while fetching profile");
    }
  },

  async updateProfile(profileData: Partial<User>) {
    this.loading = true;
    this.error = null;

    try {
      const rawState = localStorage.getItem(AUTH_STATE_KEY);
      const authState: AuthState = rawState ? JSON.parse(rawState) : {};

      const currentUser = authState.user;
      if (!currentUser) {
        this.error = "No logged-in user found.";
        return null;
      }

      const updatedFields: Record<string, unknown> = {};

      // Detect changed fields
      for (const key in profileData) {
        const newValue: any = profileData[key as keyof User];
        const currentValue = currentUser[key as keyof User];

        if (newValue instanceof File) {
          // You should handle actual file upload to S3 or R2 elsewhere
          updatedFields[key as keyof User] = URL.createObjectURL(newValue);
        } else if (
          newValue !== undefined &&
          (typeof newValue === "object"
            ? JSON.stringify(newValue) !== JSON.stringify(currentValue)
            : newValue !== currentValue)
        ) {
          updatedFields[key as keyof User] = newValue;
        }
      }

      if (Object.keys(updatedFields).length === 0) {
        return currentUser;
      }

      const response = await axios.patch<UpdateUserResponse>(
        `${BASE_URL}/api/users/me`,
        updatedFields,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (response.status === 200) {
        const updatedUser = {
          ...currentUser,
          ...response.data.user,
        };

        const newAuthState: AuthState = {
          isAuthenticated: true,
          user: userDataFormat(updatedUser) as any,
          _lastUpdated: new Date().toISOString(),
        };
        localStorage.setItem(AUTH_STATE_KEY, JSON.stringify(newAuthState));
        authStore.user = updatedUser as any;

        return updatedUser;
      }

      return null;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        this.error = err.response?.data?.message || err.message;
      } else {
        this.error = "Something went wrong. Please try again.";
      }
      return null;
    } finally {
      this.loading = false;
    }
  },
});
